/**
 * useTimesheetsMenu — shared open/closed state for the Timesheets sub-menu
 * that sits next to the main `<Sidebar>` on timesheet routes.
 *
 * Lives in a React context so both the main `<Sidebar>` (the "Timesheets"
 * NavItem reopens the submenu when the user clicks it from anywhere) and
 * the `<TimesheetPage>` (the canvas that renders the submenu) read +
 * write the same flag without prop-drilling through `__root.tsx`.
 *
 * Persisted to `localStorage` so the user's preferred state survives a
 * reload. Defaults to open on first visit.
 */

import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'

// Deliberately keeps the pre-rename string so existing users keep their
// open/closed preference across the Performance → Timesheets renames.
const STORAGE_KEY = 'performance.subMenuOpen'

interface TimesheetsMenuContextValue {
  open: boolean
  setOpen: (next: boolean) => void
  toggle: () => void
}

const TimesheetsMenuContext = createContext<TimesheetsMenuContextValue | null>(null)

function readInitial(): boolean {
  if (typeof window === 'undefined') return true
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === null) return true
    return raw === 'true'
  } catch {
    return true
  }
}

export function TimesheetsMenuProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState<boolean>(readInitial)

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(STORAGE_KEY, String(open))
    } catch {
      // Quota / private mode — non-fatal.
    }
  }, [open])

  const setOpen = useCallback((next: boolean) => setOpenState(next), [])
  const toggle = useCallback(() => setOpenState((prev) => !prev), [])

  return (
    <TimesheetsMenuContext.Provider value={{ open, setOpen, toggle }}>
      {children}
    </TimesheetsMenuContext.Provider>
  )
}

export function useTimesheetsMenu(): TimesheetsMenuContextValue {
  const ctx = useContext(TimesheetsMenuContext)
  if (!ctx) {
    // Defensive default — components that mount outside the provider
    // (e.g. tests) get a no-op stub instead of a crash. The runtime
    // behavior degrades gracefully: open=true (visible by default), no
    // persistence, no cross-component sync.
    return {
      open: true,
      setOpen: () => undefined,
      toggle: () => undefined,
    }
  }
  return ctx
}
